'use client';

import { useCallback, useState } from 'react';
import { DEFAULT_THEME } from '@pixum/theme-provider';
import type { OrderInfoType, OrderType } from './utils/types';
import { OrderStatusForm } from './partials/OrderStatusForm';
import { OrderStatusSummary } from './partials/OrderStatusSummary';
import { OrderStatusDialog } from './partials/OrderStatusDialog';
import styles from './OrderStatus.module.css';
import type { OrderStatusProps } from './types';
import Text, { TEXT_VARIANT } from '@pixum/text';
import { checkOrderStatusAction, type CheckOrderStatusResponse } from './actions';
import type { OrderStatusRetrievedParams } from './partials/OrderStatusForm/types';
import { trackOrderStatusEvent } from 'utils';
import { getTrackingLabelForSubOrders } from './utils/getTrackingLabelForSubOrders';
import { TRACKING_LABEL } from './constants';

/**
 * OrderStatus main component - orchestrates all sub-components
 * Client Component - manages state for form submission and summary display
 *
 * Features:
 * - Integrates OrderStatusForm for order ID submission
 * - Displays OrderStatusSummary when order data is retrieved
 * - Manages help dialog (OrderStatusDialog) visibility
 * - Handles server action integration via prop
 * - Manages component state and data flow
 *
 * Architecture:
 * - Form submits order ID via server action (passed as prop)
 * - Server action returns PSI API data
 * - Summary component displays the returned order information
 * - Help dialog shows CMS content when help link is clicked
 *
 * Accessibility Features:
 * - Main landmark for primary content
 * - Semantic heading hierarchy
 * - ARIA live regions for dynamic content
 * - Focus management on summary reveal
 * - Keyboard navigation support
 */
export function OrderStatus({ data, device }: OrderStatusProps) {
  const [order, setOrder] = useState<OrderType | null>(null);
  const [orderInfo, setOrderInfo] = useState<OrderInfoType | null>(null);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  if (!data) {
    return null;
  }

  const theme = data.theme || DEFAULT_THEME;
  const constants = data.constants || {};
  const productConstants = data.productConstants || {};

  const handleSubmitOrderStatus = useCallback(
    async (orderId: string): Promise<CheckOrderStatusResponse> => {
      // Clear previous order data to prevent showing stale data when new submission fails
      setOrder(null);
      setOrderInfo(null);

      // Create FormData for server action
      const formData = new FormData();
      formData.append('orderId', orderId);

      if (data.inputError) {
        formData.append('inputErrorMessage', data.inputError);
      }

      // Add i18n validation error message if available
      if (constants['i18n_order-status_invalid_order_id_format_error_message']) {
        formData.append('validationErrorMessage', constants['i18n_order-status_invalid_order_id_format_error_message']);
      }

      return await checkOrderStatusAction(formData);
    },
    [constants, data.inputError]
  );

  const handleOrderStatusRetrieved = useCallback(
    ({ orderData, detailedOrderData, error }: OrderStatusRetrievedParams) => {
      setOrder(orderData);

      if (detailedOrderData) {
        setOrderInfo(detailedOrderData);
        trackOrderStatusEvent(getTrackingLabelForSubOrders(detailedOrderData.subOrders, orderData));
      } else if (error) {
        trackOrderStatusEvent(TRACKING_LABEL[error.code]);
      } else {
        trackOrderStatusEvent(TRACKING_LABEL[orderData.status]);
      }
    },
    []
  );

  const handleHelpClick = useCallback(() => {
    setShowHelpDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setShowHelpDialog(false);
  }, []);

  return (
    <article className={styles.orderStatus} aria-labelledby="order-status-heading">
      <section className={styles.header}>
        <div className={styles.headerText}>
          {data.headline && (
            <Text className={styles.headline} variant={TEXT_VARIANT.TITLE_2}>
              {data.headline}
            </Text>
          )}
          {data.description && <Text variant={TEXT_VARIANT.BODY}>{data.description}</Text>}
        </div>

        <div className={styles.headerAction}>
          <OrderStatusForm
            buttonText={data.buttonText || constants.i18n_order_status_button_text || 'Check Status'}
            helpLinkText={data.helpLinkText || constants.i18n_order_status_help_link}
            onHelpClick={data.helpLinkText || constants.i18n_order_status_help_link ? handleHelpClick : undefined}
            label={data.inputLabel || constants.i18n_order_status_input_label || 'Order ID'}
            onSubmitOrderStatus={handleSubmitOrderStatus}
            onOrderStatusRetrieved={handleOrderStatusRetrieved}
          />
        </div>
      </section>

      {order && (
        <OrderStatusSummary
          order={order}
          orderInfo={orderInfo}
          constants={constants}
          productConstants={productConstants}
          summaries={data.summaries}
          theme={theme}
        />
      )}

      {data.dialog && (
        <OrderStatusDialog isOpen={showHelpDialog} onClose={handleDialogClose} dialog={data.dialog} device={device} />
      )}
    </article>
  );
}
